import {
    type Department,
    type RevenueEntry,
    RevenueType,
    type TaxKey,
    formatNumber as fmtNum,
    getRange,
    monthNames,
    parseDateString,
    revenueTypeIcon,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { BreakdownItem, SeriesRow } from "@pentacode/ui/charts";
import { breakdownChart, chartSeriesColors, formatCurrency, lineChart } from "@pentacode/ui/charts";
import { activeIcon } from "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { guard } from "lit/directives/guard.js";
import { alert } from "../../elements/alert-dialog";
import "../../elements/scroller";
import { app } from "../../init";
import { Routing, routeProperty } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { colors } from "../../styles/config";
import { withUniqueNames } from "./report-charts";

const formatNumber = (val: number) => fmtNum(val, 0);

interface Segment {
    name: string;
    ledger?: string;
    taxKey?: TaxKey;
    costCenter?: string;
    department?: Department;
    months: number[];
    total: number;
    hideInCharts?: boolean;
}

type Data = Map<string, Segment>;

@customElement("ptc-reports-revenue")
export class ReportsRevenue extends Routing(StateMixin(LitElement)) {
    routePattern = /reports\/revenue(?:\/(?<type>\w+))?/;

    get routeTitle() {
        return this.venue ? `Umsatzbericht: ${this.venue.name}` : undefined;
    }

    @routeProperty({ arg: "type" })
    private _type: RevenueType;

    @state()
    private _loading = false;

    @state()
    private _entries: RevenueEntry[] = [];

    @state()
    private _filterString = "";

    @state()
    private _segmentation: "group" | "department" = "group";

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    @state()
    private _monthlyRows: SeriesRow[] = [];

    @state()
    private _totalItems: BreakdownItem[] = [];

    @state()
    private _highlight?: string;

    @query(".venue-selector")
    private _venueSelector: HTMLSelectElement;

    private _updateFilter() {
        this._filterString = this._filterInput.value.toLowerCase();
    }

    private _clearFilter() {
        this._filterString = this._filterInput.value = "";
    }

    handleRoute() {
        if (!this.venue || !app.hasAccess({ venue: this.venue })) {
            if (app.accessibleVenues.length) {
                this.go(null, { ...this.router.params, venue: app.accessibleVenues[0].id.toString() }, true);
            }
            return;
        }

        if (![RevenueType.Sales, RevenueType.Expense, RevenueType.Cashless].includes(this._type)) {
            this.redirect("reports/revenue/sales");
            return;
        }

        //Invalid date
        if (!this.date || !parseDateString(this.date)) {
            this.go(null, { ...this.router.params, date: toDateString(new Date()) }, true);
            return;
        }

        if (this._type !== RevenueType.Sales) {
            this._segmentation = "group";
        }

        void this.load();
    }

    willUpdate(changes: Map<string, unknown>) {
        if (changes.has("_entries") || changes.has("_segmentation")) {
            this._updateCharts();
        }
    }

    async load() {
        if (!this.year || !this.venue) {
            return;
        }

        const range = getRange(`${this.year}-01-01` as DateString, "year");
        const from = range.from;
        let to = range.to;

        const currMonth = getRange(toDateString(new Date()), "month");

        if (to > currMonth.to) {
            to = currMonth.to;
        }

        this._loading = true;

        try {
            this._entries = (
                await app.api.getRevenueEntries({
                    from,
                    to,
                    venue: this.venueId!,
                    type: [this._type],
                })
            ).filter((e) => e.reporting);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _selectVenue() {
        this.go(null, { venue: this._venueSelector.value, date: this.date });
    }

    private _getData(): Data {
        const data: Data = new Map<string, Segment>();

        const all = {
            name: "Gesamt",
            months: monthNames.map(() => 0),
            total: 0,
        };

        const other = {
            name: "Sonstige",
            months: monthNames.map(() => 0),
            total: 0,
        };

        for (const e of this._entries) {
            const month = parseDateString(e.date)!.getMonth();

            if (this._segmentation === "group") {
                const key = e.group
                    ? `group_${e.group.id}`
                    : `${e.type}_${e.name}_${e.ledger}_${e.taxKey}_${e.costCenter}`;
                if (!data.has(key)) {
                    data.set(key, {
                        name: e.group ? e.group.name : e.name,
                        ledger: e.ledger,
                        taxKey: e.taxKey,
                        costCenter: e.costCenter,
                        months: monthNames.map(() => 0),
                        total: 0,
                    });
                }

                data.get(key)!.months[month] += Math.abs(e.amount);
                data.get(key)!.total += Math.abs(e.amount);
            } else {
                let attributed = 0;

                for (const att of (e.group && e.group.attributions) || []) {
                    const { department } = app.getDepartment(att.department);

                    if (!department) {
                        continue;
                    }

                    const key = `${department.id}`;

                    if (!data.has(key)) {
                        data.set(key, {
                            name: department.name,
                            months: monthNames.map(() => 0),
                            total: 0,
                            department,
                        });
                    }

                    const amount = (e.amount * att.ratio) / 100;

                    attributed += amount;
                    data.get(key)!.months[month] += amount;
                    data.get(key)!.total += amount;
                }

                other.months[month] += Math.max(0, e.amount - attributed);
                other.total += Math.max(0, e.amount - attributed);
            }

            all.months[month] += Math.abs(e.amount);
            all.total += Math.abs(e.amount);
        }

        for (const [, entry] of data) {
            if (entry.months.every((val, i) => Math.abs(val) <= Math.abs(all.months[i] * 0.05))) {
                for (const [month, value] of entry.months.entries()) {
                    other.months[month] += value;
                }

                other.total += entry.total;

                entry.hideInCharts = true;
            }
        }

        if (Math.round(other.total)) {
            data.set("other", other);
        }

        data.set("all", all);

        return data;
    }

    private _updateCharts() {
        const data = this._getData();
        const all = data.get("all");
        const other = data.get("other");
        const shown = (s: Segment) => !s.hideInCharts && s.months.some((value) => Math.round(value) !== 0);
        const groups = withUniqueNames(
            [...data.values()].filter((s) => s !== all && s !== other && shown(s)),
            ["Gesamt", "Sonstige"]
        );
        const visible = other && shown(other) ? [...groups, other] : groups;
        const segments = visible.map((segment, i) => ({
            ...segment,
            color: segment.department
                ? colors[segment.department.color] || segment.department.color
                : chartSeriesColors[i % chartSeriesColors.length],
        }));

        const rows = segments.flatMap(({ name, months }) =>
            months.map((value, month) => ({ x: monthNames[month].slice(0, 3), series: name, value }))
        );

        this._monthlyRows = rows;
        this._totalItems = segments.map(({ name, total, color }) => ({ label: name, value: total, color }));
    }

    private _yearSelected(e: Event) {
        const date = parseDateString(this.date) || new Date();
        date.setFullYear(parseInt((e.target as HTMLSelectElement).value));
        this.go(null, { date: toDateString(date), venue: this._venueSelector.value });
    }

    static styles = [
        shared,
        css`
            .header {
                border-bottom: solid 1px var(--shade-2);
            }

            .main {
                position: relative;
            }

            .scroller {
                overflow: auto;
                margin: 0 0.5em;
            }

            .year-selector,
            .venue-selector {
                font-size: var(--font-size-medium);
                padding-top: 0.3em;
                padding-bottom: 0.3em;
            }

            .department-row {
                display: grid;
                grid-template-columns: 1fr 4em 4em;
                grid-gap: 0.5em;
            }

            .department-row-value {
                text-align: right;
            }

            button:not([active]) .department-row-value {
                color: var(--color-highlight);
            }

            .department-row-header {
                font-size: var(--font-size-tiny);
                color: var(--color-highlight);
                font-weight: bold;
                text-align: center;
                padding: 0.5em;
            }

            .charts {
                position: sticky;
                left: 0;
                display: flex;
                margin-top: 0.5em;
                z-index: 10;
            }

            .charts-total {
                width: 19em;
            }

            .charts-monthly {
                flex: 1;
            }

            .charts-title {
                font-size: var(--font-size-large);
                padding: 0.5em;
                font-weight: 600;
                text-align: center;
            }

            .row {
                display: grid;
                grid-template-columns: 12em repeat(13, 1fr);
                background: var(--color-bg);
                min-width: 90em;
            }

            .row:not(:last-child) {
                border-bottom: solid 1px var(--shade-1);
            }

            .column {
                text-align: right;
                padding: 0.5em;
            }

            .column.name {
                text-align: left;
            }

            :not(.header-row) > .column > * {
                flex: 1;
                font-size: var(--font-size-small);
                padding: 0.5em;
                text-align: right;
                min-width: 0;
                color: var(--color-highlight);
            }

            .column > :not(:last-child) {
                border-right: dashed 1px var(--shade-1);
            }

            .column:not(:last-child) {
                border-right: solid 1px var(--shade-1);
            }

            .header-row {
                position: sticky;
                top: 0;
                z-index: 1;
            }

            .header-column {
                text-align: left;
                position: sticky;
                left: 0;
                background: inherit;
                border-radius: inherit;
            }

            .header-row > .column {
                padding-top: 0.5em;
                font-weight: 600;
                border: none;
                text-align: center;
            }

            .header-row > .header-column {
                padding-left: 0;
                padding-bottom: 0.3em;
            }

            .filter-input input {
                font-weight: normal;
                padding-top: 0.4em;
                padding-bottom: 0.4em;
            }

            .row.total-row > .column {
                font-weight: bold;
            }

            .total-column {
                font-weight: 600;
                position: sticky;
                right: 0;
                background: inherit;
                border-left: solid 1px var(--shade-1) !important;
            }

            .row:not(.header-row):hover {
                background: #f1f1f1;
                cursor: pointer;
            }
        `,
    ];

    render() {
        if (!app.company || !this.venue) {
            return html``;
        }

        const years: number[] = [];
        for (let year = new Date().getFullYear(); year > 2010; year--) {
            years.push(year);
        }

        const data = [...this._getData().values()]
            .filter((entry) => entry.name !== "Sonstige")
            .sort((a, b) => b.total - a.total);

        return html`
            <div class="fullbleed vertical layout">
                <div class="header horizontal center-aligning layout padded-medium noprint">
                    <div class="horizontal tabs">
                        ${[RevenueType.Sales, RevenueType.Expense, RevenueType.Cashless].map(
                            (type) => html`
                                <button
                                    ?active=${this._type === type}
                                    @click=${() => this.go(`reports/revenue/${type}`)}
                                >
                                    <ptc-ui-icon
                                        name=${activeIcon(revenueTypeIcon(type), this._type === type)}
                                    ></ptc-ui-icon>
                                </button>
                            `
                        )}
                    </div>

                    <div class="stretch"></div>

                    <div class="horizontal tabs" ?hidden=${this._type !== RevenueType.Sales}>
                        <button
                            ?active=${this._segmentation === "group"}
                            @click=${() => (this._segmentation = "group")}
                        >
                            Umsatzgruppe
                        </button>
                        <button
                            ?active=${this._segmentation === "department"}
                            @click=${() => (this._segmentation = "department")}
                        >
                            Abteilung
                        </button>
                    </div>

                    <div class="stretch"></div>

                    <select
                        class="venue-selector"
                        @change=${this._selectVenue}
                        ?hidden=${app.accessibleVenues.length < 2}
                    >
                        ${app.accessibleVenues.map(
                            (venue) => html`
                                <option .value=${venue.id.toString()} ?selected=${venue.id === this.venueId}>
                                    ${venue.name}
                                </option>
                            `
                        )}
                    </select>

                    <select class="year-selector" .value=${this.year.toString()} @change=${this._yearSelected}>
                        ${years.map((year) => html` <option .value=${year.toString()}>${year}</option> `)}
                    </select>
                </div>

                <div class="scroller stretch">
                    <div class="charts">
                        <div class="charts-total vertical layout">
                            <div class="charts-title">Gesamt</div>
                            <ptc-ui-chart
                                label="Umsatz im gewählten Zeitraum nach Umsatzgruppe"
                                .height=${300}
                                .definition=${guard([this._totalItems, this._highlight], () =>
                                    breakdownChart(this._totalItems, {
                                        format: formatCurrency,
                                        legend: false,
                                        highlight: this._highlight,
                                    })
                                )}
                            ></ptc-ui-chart>
                            <ptc-ui-chart-legend
                                .items=${guard([this._totalItems], () =>
                                    this._totalItems.map(({ label, color }) => ({ label, color: color! }))
                                )}
                                .highlight=${this._highlight}
                                @chart-legend-highlight=${(e: CustomEvent<{ label: string | null }>) =>
                                    (this._highlight = e.detail.label ?? undefined)}
                            ></ptc-ui-chart-legend>
                        </div>
                        <div class="charts-monthly">
                            <ptc-ui-chart
                                label="Monatliche Umsätze nach Umsatzgruppe im gewählten Jahr"
                                .height=${350}
                                .definition=${guard([this._monthlyRows, this._totalItems, this._highlight], () =>
                                    this._monthlyRows.length
                                        ? lineChart(this._monthlyRows, {
                                              grouped: true,
                                              stacked: true,
                                              yAxis: "right",
                                              legend: false,
                                              highlight: this._highlight,
                                              format: formatCurrency,
                                              colors: Object.fromEntries(
                                                  this._totalItems.map(({ label, color }) => [label, color!])
                                              ),
                                          })
                                        : undefined
                                )}
                            ></ptc-ui-chart>
                        </div>
                    </div>

                    <div class="table">
                        <div class="row header-row">
                            <div class="column header-column">
                                <div class="filter-input right icon input">
                                    <input
                                        id="filterInput"
                                        type="text"
                                        placeholder="Suchen..."
                                        @input=${this._updateFilter}
                                    />
                                    ${this._filterString
                                        ? html`<ptc-ui-icon
                                              name="icon.action.clear"
                                              class="click icon"
                                              @click=${this._clearFilter}
                                          ></ptc-ui-icon>`
                                        : html`<ptc-ui-icon
                                              name="icon.action.search"
                                              class="icon"
                                              @click=${this._clearFilter}
                                          ></ptc-ui-icon>`}
                                </div>
                            </div>

                            ${monthNames.map(
                                (n) => html`
                                    <div class="column vertical end-justifying layout">
                                        <div>${n.slice(0, 3)}</div>
                                    </div>
                                `
                            )}

                            <div class="column vertical end-justifying layout total-column">
                                <div>Gesamt</div>
                            </div>
                        </div>

                        ${[...data.values()].map(({ name, total, months }) =>
                            !name.toLowerCase().includes(this._filterString)
                                ? null
                                : html`
                                      <div class="row ${name === "Gesamt" ? "total-row" : ""}">
                                          <div class="column header-column">${name}</div>
                                          ${months.map((val) => html` <div class="column">${formatNumber(val)}</div> `)}
                                          <div class="column total-column">${formatNumber(total)}</div>
                                      </div>
                                  `
                        )}
                    </div>
                </div>

                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }
}
